import {Component, inject, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {MasterThreadService} from "../../../services/objects/master-thread.service";
import {IConversation} from "../../../models/conversation.model";
import {MaterialModule} from "../../../material.module";
import {ReactiveFormsModule} from "@angular/forms";
import {DatePipe} from "@angular/common";
import {ChatbotsService} from "../../../services/objects/chatbots.service";
import {IChatbotOptions} from "../../../models/chatbot-options.model";
import {TabbedPageComponent} from "../../objects/tabbed-page/tabbed-page.component";
import {ConversationPreviewService} from "../conversation-preview.service";
import {AssistantsService} from "../../../services/objects/assistants.service";
import {IAssistantOptions} from "../../../models/assistant-options.model";
import {ConversationTranscriptRendererComponent} from "../shared/conversation-transcript-renderer.component";

@Component({
    selector: 'app-conversation-detail',
    templateUrl: './conversation.component.html',
    styleUrls: ['./conversation.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule, DatePipe, RouterLink, ConversationTranscriptRendererComponent]
})
export class ConversationComponent extends TabbedPageComponent implements OnInit{
    private masterThreadService = inject(MasterThreadService);
    private chatBotService = inject(ChatbotsService);
    private readonly conversationPreviewService = inject(ConversationPreviewService);
    private readonly assistantsService = inject(AssistantsService);

    threadId!: string;
    thread?: IConversation;
    isLoading: boolean = true;
    chatbot?: IChatbotOptions;
    assistantNameById: Record<string, string> = {};

    ngOnInit(): void {
        super.OnInit()
        this.threadId = this.route.snapshot.paramMap.get('id') || '';
        if (this.threadId) {
            this.masterThreadService.getMasterThreadAsync(this.threadId).subscribe(
                (data) => {
                    this.thread = data;
                    this.isLoading = false;
                    this.chatBotService.getByIdAsync(this.thread.shopAssistantId).subscribe(
                        (dat) => {
                            this.chatbot = dat;
                        }
                    );
                    this.loadAssistants();
                },
                (error) => {
                    console.error('Error fetching thread details', error);
                    this.isLoading = false;
                }
            );
        } else {
            // Invalid ID, navigate back or show an error
            this.router.navigate(['/ai/conversations']);
        }
    }

    goBack(): void {
        this.router.navigate(['/ai/conversations']);
    }

    get conversationTitle(): string {
        return this.chatbot?.name || this.thread?.email || 'Conversation';
    }

    getAssistantDisplayName(assistantId?: string): string {
        if (!assistantId) return '';
        return this.assistantNameById[assistantId] || assistantId;
    }

    getAssistantSelectionTooltip(message: { assistantSelectionReason?: string; assistantId?: string }): string {
        const reason = message.assistantSelectionReason?.trim();
        if (reason) return reason;
        return message.assistantId ? 'Assistant selected for this message.' : '';
    }

    openInChatbot(): void {
        if (!this.thread?.clientId || !this.thread?.shopAssistantId) {
            return;
        }

        this.conversationPreviewService.open({
            conversationId: this.thread.id,
            clientId: this.thread.clientId,
            chatbotId: this.thread.shopAssistantId,
            title: this.chatbot?.name || this.thread.email || 'Vis i chatbot'
        });
    }

    private loadAssistants(): void {
        this.assistantsService.getAllAsync().subscribe({
            next: (assistants: IAssistantOptions[]) => {
                this.assistantNameById = (assistants || []).reduce<Record<string, string>>((acc, assistant) => {
                    const label = assistant.name?.trim() || assistant.assistantId || assistant.id;
                    if (assistant.id) acc[assistant.id] = label;
                    if (assistant.assistantId) acc[assistant.assistantId] = label;
                    return acc;
                }, {});
            },
            error: (error) => console.error('Error fetching assistants', error)
        });
    }
}
